import type { CasOptions, CheckTokenRes, ErrCode } from './types'
import { ajax, getQueryString } from './util'

/**
 * 根据域名获取cas域名
 * @returns
 */
const getCasDomain = (): string => {
  const hostname = window.location.hostname
  if (hostname.includes('.sit.')) {
    return 'https://cas.sit.sf-express.com'
  } else if (hostname.includes('.sf-express')) {
    return 'https://cas.sf-express.com'
  } else {
    return 'https://cas.sit.sf-express.com'
  }
}
/**
 * 删除url里的某个参数
 * @param urlStr 
 * @param parameter 
 * @returns 
 */
const delUrlQuery = (urlStr:string, parameter: string): string => {
    try {
        const url = new URL(urlStr)
        url.searchParams.delete(parameter)
        return url.toString()
    } catch (error) {
        return urlStr
    }
}
/**
 * 登录类
 */
export default class CAS {
  appKey: string
  appSecret: string
  checkTokenUrl: string
  loginUrl: string
  logoutUrl: string
  casDomain: string
  serviceUrl?: string
  loginSuccess?: (res: Record<any, any>) => void

  isReturnCallBackResult?:boolean
  loginFailed?: (res: Record<any, any>, instance: Record<any, any>) => void

  constructor({
    appKey,
    appSecret,
    casDomain,
    serviceUrl,
    basePath = '/api',
    loginSuccess,
    isReturnCallBackResult = false,
    loginFailed,
  }: CasOptions) {
    // 请求base url
    const BASE_URL = `${basePath}/apis-auth/login`

    this.checkTokenUrl = `${BASE_URL}/check_token`
    this.loginUrl = `${BASE_URL}/cas`
    this.logoutUrl = `${BASE_URL}/logout`
    this.casDomain = casDomain || getCasDomain()
    this.serviceUrl = serviceUrl
    this.appKey = appKey
    this.appSecret = appSecret
    this.loginSuccess = loginSuccess
    this.isReturnCallBackResult = isReturnCallBackResult
    this.loginFailed = loginFailed
  }

  _getServiceUrl(): string {
    return delUrlQuery(sessionStorage.getItem('serviceUrl') || location.href, 'ticket')
  }

  _setServiceUrl(): string {
    return this.serviceUrl || location.href
  }

  _goToCasPage(type: string, path?: string): void {
    const main = type === 'login' ? `${this.casDomain}/cas/login` : `${this.casDomain}/cas/logout`
    location.replace(`${main}?service=${encodeURIComponent(
      path || this._getServiceUrl()
    )}`)
  }

  async login(): Promise<any> {
    const ticket = getQueryString('ticket')
    const service = this._getServiceUrl()

    if (!ticket) {
      sessionStorage.setItem('serviceUrl', this._setServiceUrl())
      this._goToCasPage('login')
      return
    }
    const { appKey, appSecret, isReturnCallBackResult = false } = this

    const res = await new Promise<Record<string, any>>((resolve) => {
      let obj = {}
      ajax('POST', this.loginUrl, {
        ticket,
        appKey,
        appSecret,
        service,
        deviceId: 'any',
        isReturnCallBackResult,
      }).then((res) => {
        obj = res?.obj || {}
        if(this.loginSuccess && typeof this.loginSuccess === 'function') {
          this.loginSuccess(res)
        }
        window.history.replaceState(history.state, '', service);
        return res
      }).catch((error) => {
        if (this.loginFailed && typeof this.loginFailed === 'function') {
          this.loginFailed(error, this)
        } else {
          this._goToCasPage('login')
        }
      }).finally(() => {
        resolve(obj)
      })
    })

    return res
  }

  /**
   * 退出登录
   */
  async logout(): Promise<any> {
      const serviceUrl = this._getServiceUrl()
      try {
        await ajax('GET', this.logoutUrl)
        sessionStorage.setItem('serviceUrl', serviceUrl)
        this._goToCasPage('logout', serviceUrl)
      } catch (error: any) {
        if (error.errorCode ===  '09020101' || error.errorCode === '09020102') {
            sessionStorage.setItem('serviceUrl', serviceUrl)
            this._goToCasPage('logout', serviceUrl)
        }
      }
  }


  /**
   * 验证token是否有效，无效则跳转登录，仅应用初始化时调用一次
   * @returns Promise<CheckTokenRes>
   */
  async checkToken(token?:string): Promise<CheckTokenRes> {
    try {
      const { obj } = await ajax('GET', this.checkTokenUrl, undefined, token)
      return obj
    } catch (error:any) {
      if (error.errorCode ===  '09020101' || error.errorCode === '09020102') {
        await this.login()
      }
      return Promise.reject(error)
    }
  }

  /**
   * http status为非200的情况下，根据errcode返回错误原因
   * @param code
   * @returns string
   */
  getCodeMsg(code: ErrCode) {
    switch (code) {
      case '09020000':
        return '网关内部错误'
      case '09020101':
        return '访问需要登录认证的接口时在请求头中没有设置token'
      case '09020102':
        return '网关根据header中设置的token找不到用户信息'
      case '09020103':
        return '登录时的ticket参数是非法的'
      case '09020107':
        return '认证中心系统繁忙，请稍后重试'
      case '09020201':
        return '禁止该APP访问'
      case '09020202':
        return '内部的API，禁止所有的APP访问'
      case '09020203':
        return '百源鉴权未通过，需要联系管理员分配权限'
      case '09020204':
        return '百源鉴权服务发生异常，需要联系百源系统管理员'
      case '09020205':
        return '第三方鉴权服务发生异常，请联系相关系统的管理员'
      case '09020206':
        return '第三方鉴权校验不通过，请检查用户权限'
      case '09020213':
        return '本地鉴权未通过，需要先为应用分配权限'
      case '09020301':
        return '请求太过频繁，达到API限流限制'
      case '09020501':
        return '网关没有该API的路由信息'
      case '09020502':
        return '后台的微服务响应超时，默认20s'
      case '09020601':
        return '登录时没有deviceid参数'
      case '09020701':
        return '请求体不是一个合法的json'
      default:
        return '未知网关错误码'
    }
  }

  /**
   * 根据状态码判断是否需要重新登录
   * @param code
   * @returns boolean
   */
  isNeedLogin(code: ErrCode) {
    if (code === '09020101' || code === '09020102') {
      return true
    } else {
      return false
    }
  }
}
